import React, { useEffect, useState, useCallback } from "react";
import { Formik, Form } from "formik";
import DataTable from "react-data-table-component";
import { useSelector } from "react-redux";
import { api } from "../../api";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import DropdownComp from "../dropdown";
import Input from "../input";
import ButtonContained from "../button";
import { StockTransferSchema } from "../../validation/ValidationSchema";
import { customStyles } from "../../styles/dataTableStyles";

const historyTableStyles = {
  ...customStyles,
  headCells: {
    style: {
      paddingTop: "16px",
      paddingBottom: "16px",
      paddingLeft: "18px",
      paddingRight: "18px",
      fontWeight: "600",
    },
  },
  cells: {
    style: {
      paddingTop: "18px",
      paddingBottom: "18px",
      paddingLeft: "18px",
      paddingRight: "18px",
    },
  },
  rows: {
    style: {
      minHeight: "68px",
    },
  },
};

const StockTransfer = () => {
  const token = useSelector((state) => state.auth.token);
  const region = useSelector((state) => state.auth.region);

  const [masterProductOptions, setMasterProductOptions] = useState([]);
  const [fromStock, setFromStock] = useState(null);
  const [toStock, setToStock] = useState(null);
  const [loadingFromStock, setLoadingFromStock] = useState(false);
  const [loadingToStock, setLoadingToStock] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyReset, setHistoryReset] = useState(false);
  const [revertingId, setRevertingId] = useState(null);

  useEffect(() => {
    api.products.getMasterData({}, token).then((res) => {
      if (res && res.code === 200) {
        const options = res.data.result.map((item) => ({
          value: item.masterProdId,
          label: item.masterProdName,
        }));
        setMasterProductOptions(options);
      }
    });
  }, [token]);

  const fetchHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const response = await api.stock.getStockTransferList(
        { pageNo: 1, pageSize: 50 },
        token
      );
      if (response && response.success) {
        setHistory(response.data.result || []);
      } else {
        setHistory([]);
      }
    } catch (error) {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory, historyReset]);

  // Region switch: previously selected products/stock/history belong to the
  // old region and are no longer valid, so clear them immediately.
  useEffect(() => {
    setFromStock(null);
    setToStock(null);
    setHistory([]);
  }, [region?.id]);

  const fetchStock = async (masterProductId, setter, setLoading) => {
    if (!masterProductId) {
      setter(null);
      return;
    }
    try {
      setLoading(true);
      const response = await api.stock.getStockAvailable(
        { masterProductId },
        token
      );
      if (response && response.success) {
        setter(response.data.stockAvailable);
      } else {
        setter(null);
        toast.error(response?.message || "Unable to fetch current stock");
      }
    } catch (error) {
      setter(null);
    } finally {
      setLoading(false);
    }
  };

  const handleRevert = (row) => {
    Swal.fire({
      title: "Revert this transfer?",
      html: `Move <b>${row.quantity}</b> unit(s) back from<br/><b>${row.toMasterProductName}</b> to <b>${row.fromMasterProductName}</b>?`,
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "rgb(112 110 115)",
      confirmButtonText: "Yes, Revert",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (!result.isConfirmed) return;

      setRevertingId(row.stockTransferId);
      api.stock
        .revertStockTransfer({ stockTransferId: row.stockTransferId }, token)
        .then((res) => {
          if (res && res.code === 200) {
            const skippedKeyIds = res.data?.skippedKeyIds || [];
            if (skippedKeyIds.length) {
              toast.warn(
                `Reverted ${res.data.revertedCount} key(s). Skipped ${skippedKeyIds.length} already sold/moved key id(s): ${skippedKeyIds.join(", ")}`
              );
            } else {
              toast.success(res.message);
            }
            setHistoryReset((prev) => !prev);
          } else {
            toast.error(res?.message || "Unable to revert stock transfer");
          }
        })
        .catch(() => {
          toast.error("!Oops, Something went wrong");
        })
        .finally(() => {
          setRevertingId(null);
        });
    });
  };

  const historyColumns = [
    { name: "Date", selector: (row) => new Date(row.createdAt).toLocaleString(), sortable: true },
    { name: "Transfer From", selector: (row) => row.fromMasterProductName },
    { name: "Transfer To", selector: (row) => row.toMasterProductName },
    { name: "Quantity", selector: (row) => row.quantity, width: "110px" },
    { name: "From Stock (After)", selector: (row) => row.fromStockAfter, width: "150px" },
    { name: "To Stock (After)", selector: (row) => row.toStockAfter, width: "140px" },
    { name: "Remarks", selector: (row) => row.remarks || "-" },
    {
      name: "Status / Action",
      width: "160px",
      cell: (row) =>
        row.revertedAt ? (
          <span style={{ color: "#ff4b4b", fontWeight: 600 }}>Reverted</span>
        ) : (
          <button
            className="btn btn-sm btn-outline-danger"
            disabled={revertingId === row.stockTransferId}
            onClick={() => handleRevert(row)}
          >
            {revertingId === row.stockTransferId ? "Reverting..." : "Revert"}
          </button>
        ),
      ignoreRowClick: true,
    },
  ];

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="sec-header">STOCK TRANSFER</h2>
      </div>

      <div className="card card-table">
        <div className="card-body gatepass-saveoutput">
          <div className="reports">
            <Formik
              key={region?.id}
              validationSchema={StockTransferSchema}
              initialValues={{
                fromMasterProductId: "",
                toMasterProductId: "",
                quantity: "",
                remarks: "",
              }}
              enableReinitialize
              onSubmit={(values, { resetForm, setSubmitting }) => {
                if (
                  values.fromMasterProductId?.value ===
                  values.toMasterProductId?.value
                ) {
                  toast.error(
                    "Transfer From and Transfer To product cannot be the same"
                  );
                  setSubmitting(false);
                  return;
                }

                if (fromStock !== null && Number(values.quantity) > fromStock) {
                  toast.error("Quantity exceeds available stock");
                  setSubmitting(false);
                  return;
                }

                const formBody = {
                  fromMasterProductId: values.fromMasterProductId.value,
                  toMasterProductId: values.toMasterProductId.value,
                  quantity: Number(values.quantity),
                  remarks: values.remarks,
                };

                Swal.fire({
                  title: "Confirm Stock Transfer",
                  html: `Transfer <b>${formBody.quantity}</b> unit(s) from<br/><b>${values.fromMasterProductId.label}</b> to <b>${values.toMasterProductId.label}</b>?`,
                  showCancelButton: true,
                  confirmButtonColor: "rgb(68, 170, 68)",
                  cancelButtonColor: "rgb(112 110 115)",
                  confirmButtonText: "Yes, Transfer",
                  cancelButtonText: "Cancel",
                }).then((result) => {
                  if (!result.isConfirmed) {
                    setSubmitting(false);
                    return;
                  }

                  api.stock
                    .transferStock(formBody, token)
                    .then((res) => {
                      setSubmitting(false);
                      if (res && res.code === 200) {
                        toast.success(res.message);
                        resetForm();
                        setFromStock(null);
                        setToStock(null);
                        setHistoryReset((prev) => !prev);
                      } else {
                        toast.error(res?.message || "Unable to transfer stock");
                      }
                    })
                    .catch(() => {
                      setSubmitting(false);
                      toast.error("!Oops, Something went wrong");
                    });
                });
              }}
            >
              {({
                handleSubmit,
                handleChange,
                values,
                touched,
                errors,
                isSubmitting,
                setFieldValue,
              }) => (
                <Form onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group col-md-4">
                      <DropdownComp
                        id="fromMasterProductId"
                        name="fromMasterProductId"
                        label="Transfer From"
                        placeholder="Search product..."
                        value={values.fromMasterProductId}
                        options={masterProductOptions}
                        onChange={(selected) => {
                          setFieldValue("fromMasterProductId", selected);
                          fetchStock(selected?.value, setFromStock, setLoadingFromStock);
                        }}
                        error={touched.fromMasterProductId && errors.fromMasterProductId}
                        errorText={errors.fromMasterProductId}
                        disabled={isSubmitting}
                      />
                      <small className="form-text text-muted">
                        {loadingFromStock
                          ? "Loading current stock..."
                          : fromStock !== null
                          ? `Current unused stock: ${fromStock}`
                          : ""}
                      </small>
                    </div>

                    <div className="form-group col-md-4">
                      <DropdownComp
                        id="toMasterProductId"
                        name="toMasterProductId"
                        label="Transfer To"
                        placeholder="Search product..."
                        value={values.toMasterProductId}
                        options={masterProductOptions}
                        onChange={(selected) => {
                          setFieldValue("toMasterProductId", selected);
                          fetchStock(selected?.value, setToStock, setLoadingToStock);
                        }}
                        error={touched.toMasterProductId && errors.toMasterProductId}
                        errorText={errors.toMasterProductId}
                        disabled={isSubmitting}
                      />
                      <small className="form-text text-muted">
                        {loadingToStock
                          ? "Loading current stock..."
                          : toStock !== null
                          ? `Current unused stock: ${toStock}`
                          : ""}
                      </small>
                    </div>

                    <div className="form-group col-md-4">
                      <Input
                        id="quantity"
                        name="quantity"
                        placeholder="Quantity to transfer"
                        type="number"
                        min="1"
                        label="Quantity"
                        value={values.quantity}
                        onChange={handleChange}
                        error={touched.quantity && errors.quantity}
                        errorText={errors.quantity}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group col-md-8">
                      <Input
                        id="remarks"
                        name="remarks"
                        placeholder="Remarks (optional)"
                        type="text"
                        label="Remarks"
                        value={values.remarks}
                        onChange={handleChange}
                        error={touched.remarks && errors.remarks}
                        errorText={errors.remarks}
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <ButtonContained buttonName="Transfer Stock" disabled={isSubmitting} />
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      <div className="table-head mt-4">
        <h4 className="sec-header">TRANSFER HISTORY</h4>
      </div>
      <DataTable
        columns={historyColumns}
        data={history}
        progressPending={historyLoading}
        pagination
        highlightOnHover
        persistTableHead
        customStyles={historyTableStyles}
        noDataComponent="No stock transfer history found"
      />
    </div>
  );
};

export default StockTransfer;
